/**
 * SIP REFER helper for call transfer (F4.2).
 *
 * referBlind: RFC 5589 attended-less transfer. Sends REFER to the SBC asking
 * it to bridge the current dialog to a new target. After SBC accepts, the
 * agent's leg is terminated by the SBC.
 *
 * Warm/attended transfer (RFC 3891 REFER replaces) is handled by
 * `WrappedSession.referReplaces(consult)` in `ua.ts` — it uses JsSIP's
 * native `refer(target, { replaces })` API, which extracts dialog tags
 * internally so we don't need to manage them here.
 */

export interface ReferTransport {
  refer(
    target: string,
    opts: { eventHandlers: { accepted: () => void; failed: (e: { cause?: string }) => void } },
  ): void;
}

export interface ReferBlindDeps {
  /** SBC domain to construct the SIP URI. */
  domain: string;
}

export class TransferRejectedError extends Error {
  override readonly name = 'TransferRejectedError';
  constructor(public readonly reason: string) {
    super(`SIP transfer rejected: ${reason}`);
  }
}

const NOTIFY_TIMEOUT_MS = 10_000;

export function referBlind(
  session: ReferTransport,
  target: string,
  deps: ReferBlindDeps,
): Promise<void> {
  const targetUri = `sip:${target}@${deps.domain}`;
  return new Promise((resolve, reject) => {
    let settled = false;
    const settle = (fn: () => void): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      fn();
    };
    const timeout = setTimeout(() => {
      settle(() => reject(new TransferRejectedError('SBC NOTIFY timeout')));
    }, NOTIFY_TIMEOUT_MS);
    session.refer(targetUri, {
      eventHandlers: {
        accepted: () => settle(() => resolve()),
        failed: (e) => settle(() => reject(new TransferRejectedError(e.cause ?? 'unknown'))),
      },
    });
  });
}

